import { ArrowLeft, Clock, Phone, Search, Trash2, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../config/axios-conf';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSocket } from '../../../services/socket.service';
import styles from '../../../assets/scss/pages/WaitingCustomers.module.scss';

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

function getAvatarColor(name) {
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function normalize(str) {
    return (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, '')
        .toLowerCase();
}

function fmtCheckinTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    return d.toLocaleString('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
    });
}

function timeWaiting(str) {
    if (!str) return '';
    const diff = Math.floor((Date.now() - new Date(str).getTime()) / 60000);
    if (diff < 1) return 'Vừa vào';
    if (diff < 60) return `${diff} phút`;
    return `${Math.floor(diff / 60)}h${diff % 60 > 0 ? ` ${diff % 60}p` : ''}`;
}

const TYPE_MAP = {
    new:    { label: 'Mới',    bg: '#0098F6' },
    vip:    { label: 'VIP',    bg: '#F59E0B' },
    normal: { label: 'Thường', bg: '#22C55E' },
};

function sortByCheckinAsc(items) {
    return [...items].sort((a, b) => new Date(a.checkinTime) - new Date(b.checkinTime));
}

export default function WaitingCustomers() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCheckin     = hasPermission(PERMISSIONS.CUSTOMER.CHECKIN);
    const canCreateOrder = hasPermission(PERMISSIONS.BILL.CREATE);

    const [list, setList]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [confirmId, setConfirmId] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/customers/checkinAll', { params: { limit: 1000 } });
            const items = (data.data || []).map((c) => ({
                id:          c.customer.id,
                name:        c.customer.name,
                phone:       c.customer.phone,
                type:        c.customer.type,
                point:       c.customer.point,
                checkinTime: c.checkinTime,
            }));
            setList(sortByCheckinAsc(items));
        } catch (err) {
            console.error('Lỗi fetch checkin:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Realtime socket
    useEffect(() => {
        const socket = getSocket();

        const onCreated = (checkin) => {
            const customer = checkin.customer || checkin.Customer || {};
            if (!customer?.id) return;
            const item = {
                id:          customer.id,
                name:        customer.name,
                phone:       customer.phone,
                type:        customer.type,
                point:       customer.point,
                checkinTime: checkin.checkinTime || new Date().toISOString(),
            };
            setList((prev) => {
                const idx = prev.findIndex((c) => c.id === item.id);
                const next = idx >= 0
                    ? prev.map((c) => (c.id === item.id ? item : c))
                    : [...prev, item];
                return sortByCheckinAsc(next);
            });
        };

        const onDeleted = (customerId) => {
            const id = typeof customerId === 'object' ? customerId?.id : customerId;
            setList((prev) => prev.filter((c) => c.id !== id));
        };

        socket.on('checkin.created', onCreated);
        socket.on('checkin.deleted', onDeleted);
        return () => {
            socket.off('checkin.created', onCreated);
            socket.off('checkin.deleted', onDeleted);
        };
    }, []);

    const handleDelete = async (id) => {
        try {
            await api.delete(`/customers/checkin/${id}`);
            setList((prev) => prev.filter((c) => c.id !== id));
        } catch (err) {
            console.error('Lỗi xoá checkin:', err);
        } finally {
            setConfirmId(null);
        }
    };

    const handleCreateBill = (item) => {
        if (!canCreateOrder) return;
        navigate('/admin/bills/create', { state: { preselectedCustomer: item } });
    };

    const q = normalize(search);
    const filtered = list.filter((c) =>
        !q || normalize(c.name).includes(q) || normalize(c.phone || '').includes(q)
    );

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate('/admin/dashboard')}>
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className={styles.title}>Đợi phục vụ</h1>
                        <div className={styles.subtitle}>{list.length} khách đang chờ</div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <Search size={16} />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Tìm tên, số điện thoại..."
                    />
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner} />
                    <span>Đang tải...</span>
                </div>
            ) : filtered.length === 0 ? (
                <div className={styles.empty}>
                    <Users size={40} className={styles.emptyIcon} />
                    <span>Chưa có khách nào đang chờ</span>
                </div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 48, textAlign: 'center' }}>#</th>
                                <th>Khách hàng</th>
                                <th>Số điện thoại</th>
                                <th>Thời gian vào</th>
                                <th>Chờ</th>
                                <th>Điểm</th>
                                <th style={{ width: 52 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((item, idx) => {
                                const typeInfo = TYPE_MAP[item.type] || TYPE_MAP.normal;
                                return (
                                    <tr
                                        key={item.id}
                                        className={canCreateOrder ? styles.rowClickable : undefined}
                                        onClick={() => handleCreateBill(item)}
                                    >
                                        <td style={{ textAlign: 'center', color: '#6B7280' }}>{idx + 1}</td>
                                        <td>
                                            <div className={styles.customerCell}>
                                                <div
                                                    className={styles.avatar}
                                                    style={{ background: getAvatarColor(item.name) }}
                                                >
                                                    {(item.name || '?')[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className={styles.customerName}>{item.name}</div>
                                                    <span
                                                        className={styles.typeBadge}
                                                        style={{ background: typeInfo.bg }}
                                                    >
                                                        {typeInfo.label}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.phoneCell}>
                                                <Phone size={13} />
                                                {item.phone || '—'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.timeCell}>
                                                <Clock size={13} />
                                                {fmtCheckinTime(item.checkinTime)}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={styles.waitBadge}>
                                                {timeWaiting(item.checkinTime)}
                                            </span>
                                        </td>
                                        <td className={styles.pointCell}>
                                            {Number(item.point || 0).toLocaleString('vi-VN')} đ
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {canCheckin && (
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => setConfirmId(item.id)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Confirm delete modal */}
            {confirmId && (
                <div className={styles.overlay} onClick={() => setConfirmId(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalTitle}>Xoá khỏi danh sách chờ?</div>
                        <div className={styles.modalDesc}>
                            Khách hàng sẽ bị xoá khỏi danh sách đợi phục vụ.
                        </div>
                        <div className={styles.modalActions}>
                            <button className={styles.btnCancel} onClick={() => setConfirmId(null)}>
                                Huỷ
                            </button>
                            <button className={styles.btnDelete} onClick={() => handleDelete(confirmId)}>
                                Xoá
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
